import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import { Alert, Button } from '@/components/ds';
import { useSubscription } from '@/hooks/useSubscription';
import { settingsSectionPath } from '@/routes/route-config';

/** Fired by the API client when a change is refused with SUBSCRIPTION_EXPIRED. */
export const READ_ONLY_EVENT = 'dc:subscription-read-only';

/**
 * A lapsed clinic (subscription expired, grace over) stays open read-only:
 * every record can still be viewed and exported, changes are refused by the
 * API. This says so on every page, with the way back to billing, and cannot
 * be dismissed. A refusal while the cached status still says "active" (the
 * clinic lapsed mid-session) refreshes the status so the banner appears.
 */
export function ReadOnlyBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { isBlocked } = useSubscription();

  useEffect(() => {
    const onRefused = () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    };
    window.addEventListener(READ_ONLY_EVENT, onRefused);
    return () => window.removeEventListener(READ_ONLY_EVENT, onRefused);
  }, [queryClient]);

  // The billing page carries its own read-only notice.
  const billingPath = settingsSectionPath('subscription');
  if (!isBlocked || pathname === billingPath) return null;

  return (
    <div
      className="px-4 pt-3 md:px-6 print:hidden"
      data-testid="read-only-banner"
    >
      <Alert tone="danger" title={t('subscription.readOnly.title')}>
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span>{t('subscription.readOnly.subline')}</span>
          <Button
            size="sm"
            variant="danger"
            startIcon={<CreditCard aria-hidden />}
            onPress={() => navigate(billingPath)}
          >
            {t('subscription.warning.renewNow')}
          </Button>
        </span>
      </Alert>
    </div>
  );
}
